// @flow

import React from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { compose } from "redux";
import classNames from "classnames";
import { ColumnsContainer, Button, SectionComponent } from "@components";
import actionCreators from "@modules/shared/actionCreator";
import type { State } from "@store/reducers";
import type { PropertiesSchema } from "@types";
import {
  getAbsenceDetails,
  getAddAbsenceModal,
  getAbsencesSchema,
} from "@modules/shared/selectors/employee-absences";
import type { EmployeeAbsence } from "@modules/shared/reducer/employee-absences/types";
import type { EmployeeType } from "@modules/employee/types";
import { formatDateTime } from "@transformers/date";
import { AddEmployeeAbsenceModal } from "@modules/shared/components";
import type { AddEmployeeAbsenceModal as AddAbsenceModalType } from "@modules/shared/reducer/employee-absences/types";
import { getEmployee } from "@modules/employee/selectors";

type OwnProps = $Exact<{
  clientId: number,
  employeeId: number,
  absenceId: number,
  canEditAbsence?: boolean,
  isReportingSection?: boolean,
  isClientAdmin: boolean,
}>;

type PropsFromState = $Exact<{
  absence: EmployeeAbsence | null,
  addAbsenceModal: AddAbsenceModalType,
  absencesSchema: ?PropertiesSchema,
  employee: EmployeeType,
}>;

type PropsFromDispatch = $Exact<{
  getEmployeeAbsence: typeof actionCreators.GetEmployeeAbsence,
  updateEmployeeAbsence: typeof actionCreators.UpdateEmployeeAbsence,
  updateAbsenceAddModalData: typeof actionCreators.UpdateAbsenceAddModalData,
  toggleAddAbsenceModal: typeof actionCreators.ToggleAddAbsenceModal,
  getEmployeeAbsencesSchema: typeof actionCreators.GetEmployeeAbsencesSchema,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

export class EmployeeAbsenceDetails extends React.Component<Props> {
  componentDidMount() {
    this.props.getEmployeeAbsence({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      absenceId: this.props.absenceId,
      isReportingRequest: this.props.isReportingSection,
    });

    if (!this.props.absencesSchema) {
      this.props.getEmployeeAbsencesSchema({
        clientId: this.props.clientId,
        employeeId: this.props.employeeId,
      });
    }
  }

  showEditAbsenceModal = () => {
    this.props.toggleAddAbsenceModal({
      visible: true,
      editing: true,
      absence: this.props.absence,
    });
  };

  closeEditAbsenceModal = () => {
    this.props.toggleAddAbsenceModal({
      visible: false,
    });
  };

  handleFormSubmit = ({ formData }: Object) => {
    const absence = {
      ...this.props.absence,
      ...formData,
    };

    this.props.updateEmployeeAbsence({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
      formData: absence,
    });
    this.closeEditAbsenceModal();
  };

  get absenceDetailsRows() {
    const {
      absenceType,
      absenceReason,
      isWorkRelated,
      isAccidentRelated,
      alertsTriggered,
      hasConsentToShare,
      id,
      status,
      createdAt,
      closeAt,
      startsAt,
      fitAt,
      rtwAt,
    } = this.props.absence || {};

    const thirdColumnData = [
      [
        this.props.t("ABSENCE_TYPE"),
        absenceType ? (
          <div
            key="absenceType"
            className={absenceType === "MEDICAL" ? "medical-badge" : "non-medical-badge"}
          >
            {this.props.t(absenceType)}
          </div>
        ) : (
          this.props.t("N/A")
        ),
      ],
      [
        this.props.t("ABSENCE_REASON"),
        this.props.t((absenceReason && absenceReason.name) || "N/A"),
      ],
    ];

    const fourthColumnData = [
      [this.props.t("WORK_RELATED_HEADER"), this.props.t(isWorkRelated ? "YES" : "NO")],
      [this.props.t("ACCIDENT_RELATED_HEADER"), this.props.t(isAccidentRelated ? "YES" : "NO")],
      [this.props.t("ALERT_TRIGGERED"), alertsTriggered],
    ];

    if (this.props.canEditAbsence) {
      thirdColumnData.push([
        this.props.t("CONSENT_GIVEN"),
        <span
          key="hasConsentToShare"
          className={classNames("font-weight-bold", {
            "text-negative": !hasConsentToShare,
            "text-light-green": hasConsentToShare,
          })}
        >
          {this.props.t(hasConsentToShare ? "YES" : "NO")}
        </span>,
      ]);
    }

    return [
      {
        columns: [
          {
            columnTitle: this.props.t("ABSENCE_MAIN_DETAILS"),
            data: [
              [this.props.t("ABSENCE_REASONS_ID"), id],
              [
                this.props.t("STATUS"),
                status ? (
                  <div
                    key="absenceStatus"
                    className={status === "OPEN" ? "open-badge" : "active-badge"}
                  >
                    {this.props.t(status)}
                  </div>
                ) : (
                  this.props.t("N/A")
                ),
              ],
              [this.props.t("DATE_REPORTED"), formatDateTime(createdAt)],
              [this.props.t("DATE_CLOSED"), formatDateTime(closeAt)],
            ],
          },
          {
            columnTitle: this.props.t("START_FIT_RTW_DATES"),
            data: [
              [this.props.t("START_DATE_AND_TIME"), formatDateTime(startsAt)],
              [this.props.t("FIT_DATE_AND_TIME"), formatDateTime(fitAt)],
              [this.props.t("RTW_DATE_AND_TIME"), formatDateTime(rtwAt)],
            ],
          },
          {
            columnTitle: this.props.t("ABSENCE_REASON"),
            data: [...thirdColumnData],
          },
          {
            columnTitle: this.props.t("MORE_INFORMATION"),
            data: [...fourthColumnData],
          },
        ],
      },
    ];
  }

  handleFormChange = ({ formData }: Object) => {
    this.props.updateAbsenceAddModalData(formData);
  };

  get editDetails() {
    const { absence, canEditAbsence } = this.props;
    let canEdit = !!canEditAbsence;

    if (!absence) return false;

    if (canEdit) {
      // client admin and no consent to share
      if (this.props.isClientAdmin && !(this.props.absence || {}).absenceReason?.id) {
        canEdit = false;
      }
    }

    return canEdit;
  }

  render() {
    return (
      <SectionComponent
        wrapperClassName="flex-column"
        title={<h5>{this.props.t("ABSENCE_DETAILS")}</h5>}
        rightLink={
          this.editDetails ? (
            <Button onClick={this.showEditAbsenceModal}>{this.props.t("EDIT_DETAILS")}</Button>
          ) : null
        }
      >
        <ColumnsContainer className="rounded-top" rows={this.absenceDetailsRows} />
        {this.props.addAbsenceModal.visible && (
          <AddEmployeeAbsenceModal
            onDismissHandler={this.closeEditAbsenceModal}
            onSubmit={this.handleFormSubmit}
            onChange={this.handleFormChange}
            schema={this.props.absencesSchema}
            editing={this.props.addAbsenceModal.editing}
            absence={this.props.addAbsenceModal.absence}
            absenceId={this.props.absenceId}
            employee={this.props.employee}
          />
        )}
      </SectionComponent>
    );
  }
}

const mapStateToProps = (state: State, ownProps: OwnProps): PropsFromState => ({
  absence: getAbsenceDetails(state),
  addAbsenceModal: getAddAbsenceModal(state),
  absencesSchema: getAbsencesSchema(state),
  employee: getEmployee(ownProps.employeeId)(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getEmployeeAbsence: actionCreators.GetEmployeeAbsence,
  updateEmployeeAbsence: actionCreators.UpdateEmployeeAbsence,
  updateAbsenceAddModalData: actionCreators.UpdateAbsenceAddModalData,
  toggleAddAbsenceModal: actionCreators.ToggleAddAbsenceModal,
  getEmployeeAbsencesSchema: actionCreators.GetEmployeeAbsencesSchema,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(EmployeeAbsenceDetails);
